export class Money {
  private cents: number;

  constructor(value: number | string) {
    this.cents = Money.parseToCents(value);
    if (this.cents < 0) {
      throw new Error("Money cannot be negative");
    }
  }

  private static parseToCents(value: number | string): number {
    const str = value.toString();

    if (!str.includes(".")) {
      return Number(str) * 100;
    }

    const [whole, decimal] = str.split(".");
    const cents = (decimal + "00").slice(0, 2); // ensure 2 digits

    return Number(whole) * 100 + Number(cents);
  }

  private static fromCents(cents: number): Money {
    const instance = Object.create(Money.prototype);
    instance.cents = cents;
    return instance;
  }

  public add(value: Money | number | string): Money {
    const other =
      value instanceof Money ? value.cents : Money.parseToCents(value);
    return Money.fromCents(this.cents + other);
  }

  public subtract(value: Money | number | string): Money {
    const other =
      value instanceof Money ? value.cents : Money.parseToCents(value);
    return Money.fromCents(this.cents - other);
  }

  public multiply(value: number | Money): Money {
    const factor = value instanceof Money ? value.toNumber() : value;
    const result = Math.trunc(this.cents * factor);
    return Money.fromCents(result);
  }

  public divide(divisor: number): Money {
    if (divisor === 0) throw new Error("Cannot divide by zero");
    const result = Math.trunc(this.cents / divisor); // Truncate, don't round
    return Money.fromCents(result);
  }

  public equals(value: Money | number | string): boolean {
    const other =
      value instanceof Money ? value.cents : Money.parseToCents(value);
    return this.cents === other;
  }

  public greaterThan(value: Money | number | string): boolean {
    const other =
      value instanceof Money ? value.cents : Money.parseToCents(value);
    return this.cents > other;
  }

  public lessThan(value: Money | number | string): boolean {
    const other =
      value instanceof Money ? value.cents : Money.parseToCents(value);
    return this.cents < other;
  }

  public toNumber(): number {
    return this.cents / 100;
  }

  public toString(): string {
    return this.toNumber().toFixed(2);
  }

  public getCents(): number {
    return this.cents;
  }
}
